import "./index.scss";
import "pages/StandardTreatment/shared/masterData.scss";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { FiAlertCircle, FiArrowLeft, FiArrowRight, FiCopy, FiSend } from "react-icons/fi";
import Button from "components/atoms/Button";
import standardTreatmentService, { errorMessages } from "services/StandardTreatment";
import useServiceData from "pages/StandardTreatment/shared/useServiceData";
import useBootstrapModal from "pages/StandardTreatment/shared/useBootstrapModal";
import useRuleEditor from "pages/StandardTreatment/shared/useRuleEditor";
import useTherapyMasters from "pages/StandardTreatment/shared/useTherapyMasters";
import ReleaseModal, { RELEASE_MODAL_ID } from "pages/StandardTreatment/shared/ReleaseModal";
import SaveStatus from "pages/StandardTreatment/shared/SaveStatus";
import { ActivePill, ErrorState, ReadOnlyBanner, VersionStatusPill } from "pages/StandardTreatment/shared/ui";
import ConfigStepper from "./components/ConfigStepper";
import VersionInfoStep from "./components/VersionInfoStep";
import ClinicalConditionStep from "./components/ClinicalConditionStep";
import TherapyDoseStep from "./components/TherapyDoseStep";
import ValidationStep from "./components/ValidationStep";
import SimulationStep from "./components/SimulationStep";
import RuleSelector from "./components/RuleSelector";
import { STEPS } from "./constants";
import { hasTherapyInputErrors } from "./validation";

// GET /standard-treatment-rules dibatasi per halaman; Versi 1 punya 186 rule → ambil semua halaman.
const loadAllRules = async (version_id) => {
  const rows = [];
  for (let page = 1; page; ) {
    const res = await standardTreatmentService.listRules({ version_id, page, limit: 100 });
    rows.push(...res.standard_treatment_rules);
    page = res.nextPage;
  }
  return rows;
};

const RELEASE_HELPER = "Rilis tersedia setelah validasi (langkah 4) lulus tanpa findings.";

const MasterPengobatanStandar = () => {
  const history = useHistory();
  const params = new URLSearchParams(useLocation().search);
  const versionId = params.get("version_id");
  const [step, setStep] = useState(Number(params.get("step")) || 1);
  const [maxReached, setMaxReached] = useState(Number(params.get("step")) || 1);
  const [showErrors, setShowErrors] = useState({});
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [validation, setValidation] = useState({ loading: false, data: null, error: null });
  const pageRef = useRef(null);
  const releaseModal = useBootstrapModal(RELEASE_MODAL_ID);

  useEffect(() => {
    document.title = "SISMAL | Konfigurasi Pengobatan Standar";
  }, []);

  // Tanpa version_id: buka Draft terbaru, atau Versi aktif bila tidak ada Draft.
  const fallback = useServiceData(
    () =>
      versionId
        ? Promise.resolve(null)
        : Promise.all([
            standardTreatmentService.listVersions({ status: "DRAFT", page: 1, limit: 1 }),
            standardTreatmentService.getActiveRelease().catch(() => null),
          ]).then(([drafts, active]) => drafts.version_rules[0]?.id || active?.version_id || ""),
    [versionId]
  );
  useEffect(() => {
    if (!versionId && fallback.data) history.replace(`/master-pengobatan-standar?version_id=${fallback.data}`);
  }, [versionId, fallback.data, history]);

  const version = useServiceData(
    () => (versionId ? standardTreatmentService.getVersion(versionId) : Promise.resolve(null)),
    [versionId]
  );
  const active = useServiceData(() => standardTreatmentService.getActiveRelease(), []);
  const rules = useServiceData(() => (versionId ? loadAllRules(versionId) : Promise.resolve([])), [versionId]);
  const summary = useServiceData(
    () => (versionId ? standardTreatmentService.getRulesSummary(versionId) : Promise.resolve(null)),
    [versionId, step]
  );
  const masters = useTherapyMasters();

  const v = version.data;
  const readOnly = v?.status !== "DRAFT";
  const isActive = Boolean(v && active.data?.version_id === v.id);
  const ruleList = rules.data || [];
  const editor = useRuleEditor(selectedRuleId, { readOnly });

  useEffect(() => {
    if (ruleList.length && !ruleList.some((r) => r.id === selectedRuleId)) setSelectedRuleId(ruleList[0].id);
  }, [ruleList, selectedRuleId]);

  const runValidation = useCallback(() => {
    if (!versionId) return;
    setValidation((prev) => ({ ...prev, loading: true, error: null }));
    standardTreatmentService
      .getValidation(versionId)
      .then((data) => setValidation({ loading: false, data, error: null }))
      .catch((err) => setValidation({ loading: false, data: null, error: errorMessages(err) }));
  }, [versionId]);

  // Validasi dijalankan setiap kali langkah 4 dibuka (data bisa berubah di langkah lain).
  useEffect(() => {
    if (step === 4) runValidation();
  }, [step, runValidation]);

  // Input terapi yang belum lengkap tidak tersimpan (akan ditolak 400) → tahan di langkah 3.
  const therapyPending = !readOnly && editor.rule && hasTherapyInputErrors(editor.rule.rule_therapies);
  const hasErrors = (s) => s === 3 && therapyPending;
  const canRelease = !readOnly && validation.data?.valid === true;

  const statuses = STEPS.map(({ id }) => {
    if (!v) return "disabled";
    if (id === step) return "current";
    if (id === 4 && validation.data && !validation.data.valid) return "error";
    if (hasErrors(id)) return "error";
    return id <= maxReached ? "completed" : "available";
  });

  const goTo = (target) => {
    if (!v) return;
    setStep(target);
    setMaxReached((prev) => Math.max(prev, target));
    // Yang scroll adalah .page-inner (MainApp), bukan window.
    pageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const next = () => {
    if (hasErrors(step)) {
      setShowErrors((prev) => ({ ...prev, [step]: true }));
      return;
    }
    goTo(step + 1);
  };

  const selectRule = (id) => {
    if (editor.saveStatus !== "pending") return setSelectedRuleId(id);
    confirmAlert({
      title: "Terapi belum lengkap",
      message: "Perubahan terapi yang belum lengkap pada rule ini belum tersimpan dan akan dibuang.",
      buttons: [{ label: "Buang & pindah", onClick: () => setSelectedRuleId(id) }, { label: "Tetap di sini" }],
    });
  };

  const cloneThis = () =>
    standardTreatmentService
      .cloneVersion(v.id)
      .then((r) => {
        toast.success(`Versi ${v.version} diklon menjadi Draft Versi ${r.version}`);
        history.push(`/master-pengobatan-standar?version_id=${r.id}`);
      })
      .catch((err) => toast.error(errorMessages(err).join(" · ")));

  // ---------------------------------------------------------------- empty / error / loading
  if (!versionId && !fallback.loading && fallback.data === "")
    return (
      <div className="master-data-page">
        <h1 className="page-title">Konfigurasi Pengobatan Standar</h1>
        <div className="mps-empty mt-3">
          Belum ada Draft maupun Versi aktif. Buat Draft dari{" "}
          <Link to="/standard-treatment/versions">Daftar Versi</Link>.
        </div>
      </div>
    );
  const loadError = fallback.error || version.error || rules.error;
  if (loadError)
    return (
      <div className="master-data-page">
        <ErrorState messages={loadError} onRetry={() => Promise.all([version.reload(), rules.reload()])} />
      </div>
    );

  const current = STEPS[step - 1];
  const selectedRule = ruleList.find((r) => r.id === selectedRuleId);

  const ruleEditorBody = (render) => {
    if (!ruleList.length)
      return (
        <div className="mps-empty">
          Versi ini belum punya rule.{" "}
          {!readOnly && <Link to={`/standard-treatment/rules?version_id=${versionId}`}>Tambah rule di Daftar Rules</Link>}
        </div>
      );
    if (editor.error) return <ErrorState messages={editor.error} onRetry={editor.reload} />;
    if (!editor.rule || editor.loading) return <p className="mps-muted">Memuat rule...</p>;
    return render(editor.rule);
  };

  return (
    <div className="mps-page" ref={pageRef}>
      <header className="mps-card mps-header">
        <div className="mps-header-main">
          <span className="mps-avatar" aria-hidden="true">
            S
          </span>
          <div className="mps-header-text">
            <h1 className="mps-title">Master Pengobatan Standar</h1>
            <p className="mps-subtitle">
              Konfigurasi Aturan
              {v && (
                <>
                  {" "}
                  · Versi {v.version} <VersionStatusPill status={v.status} /> {isActive && <ActivePill />}
                  {v.clone_from_version && <> · diklon dari Versi {v.clone_from_version}</>}
                </>
              )}
            </p>
          </div>
        </div>
        {v && (
          <div className="mps-header-actions">
            {readOnly ? (
              <Button type="button" variant="primary" size="normal" onClick={cloneThis}>
                <FiCopy size={16} className="mr-2" />
                Klon jadi Draft
              </Button>
            ) : (
              <>
                {selectedRule && <SaveStatus status={editor.saveStatus} errors={editor.saveErrors} />}
                <div className="mps-publish">
                  <span title={canRelease ? undefined : RELEASE_HELPER}>
                    <Button type="button" variant="primary" size="normal" isDisabled={!canRelease} onClick={() => releaseModal.open("PUBLISH", v)}>
                      <FiSend size={16} className="mr-2" />
                      Rilis
                    </Button>
                  </span>
                  {!canRelease && <small className="mps-helper">{RELEASE_HELPER}</small>}
                </div>
              </>
            )}
          </div>
        )}
      </header>

      <div className="mps-card mps-stepper-card">
        <ConfigStepper steps={STEPS} statuses={statuses} onStepClick={goTo} />
      </div>

      <main className="mps-card">
        {!v ? (
          <p className="mps-muted mb-0">Memuat versi...</p>
        ) : (
          <>
            <h2 className="mps-step-title">
              <span className="mps-muted">Langkah {step} dari {STEPS.length} ·</span> {current.title}
            </h2>

            {readOnly && step !== 5 && <ReadOnlyBanner version={v} />}

            {showErrors[step] && hasErrors(step) && (
              <div className="mps-alert is-error" role="alert">
                <FiAlertCircle size={20} className="mps-alert-icon" />
                <div>
                  <p className="mps-alert-title">Lengkapi terapi rule ini</p>
                  <p className="mps-alert-text">Terapi yang belum lengkap belum tersimpan ke server.</p>
                </div>
              </div>
            )}

            {step === 1 && <VersionInfoStep version={v} isActive={isActive} summary={summary.data} />}
            {(step === 2 || step === 3) && (
              <RuleSelector
                rules={ruleList}
                selectedId={selectedRuleId}
                onSelect={selectRule}
                versionId={versionId}
                disabled={editor.saveStatus === "saving"}
              />
            )}
            {step === 2 &&
              ruleEditorBody((rule) => (
                <ClinicalConditionStep
                  key={rule.id}
                  conditions={rule.rule_conditions}
                  onChange={editor.setConditions}
                  readOnly={readOnly}
                  showErrors={showErrors[2]}
                />
              ))}
            {step === 3 &&
              (masters.error ? (
                <ErrorState messages={masters.error} onRetry={masters.reload} />
              ) : (
                ruleEditorBody((rule) => (
                  <TherapyDoseStep
                    therapies={rule.rule_therapies}
                    onChange={editor.setTherapies}
                    readOnly={readOnly}
                    showErrors={showErrors[3]}
                    masters={masters}
                  />
                ))
              ))}
            {step === 4 && (
              <ValidationStep versionId={versionId} validation={validation} onRevalidate={runValidation} onGoToStep={goTo} />
            )}
            {step === 5 && <SimulationStep versionId={versionId} drugs={masters.drugs} />}

            <footer className="mps-footer">
              {step > 1 ? (
                <Button type="button" variant="secondary" size="normal" onClick={() => goTo(step - 1)}>
                  <FiArrowLeft size={16} className="mr-2" />
                  Kembali
                </Button>
              ) : (
                <span />
              )}
              {step < STEPS.length && (
                <Button type="button" variant="primary" size="normal" onClick={next}>
                  {step === 4 ? "Lanjut ke Simulasi" : "Selanjutnya"}
                  <FiArrowRight size={16} className="ml-2" />
                </Button>
              )}
            </footer>
          </>
        )}
      </main>

      <ReleaseModal
        key={releaseModal.modal.openCount}
        version={releaseModal.modal.item}
        releaseType="PUBLISH"
        onReleased={(result) => {
          releaseModal.close();
          toast.success(`Rilis #${result.release_no}: Versi ${result.version} kini FINAL dan aktif`);
          Promise.all([version.reload(), active.reload()]);
          setValidation({ loading: false, data: null, error: null });
        }}
      />
    </div>
  );
};

export default MasterPengobatanStandar;
